/** Colunas fixas de toda linha de cliente impactado (contrato backend ↔ front). */
import {
    coerceToFiniteNumber,
    formatCurrencyBRL,
    formatNumberPtBR,
    formatPercentPtBR,
} from "src/lib/utils/numberFormat";

export const FIXED_IMPACTED_CLIENT_COLUMNS = [
    "account",
    "name",
    "advisor_name",
    "volumeOportunidade",
] as const;

export type FixedImpactedClientColumn = (typeof FIXED_IMPACTED_CLIENT_COLUMNS)[number];

const FIXED_SET = new Set<string>(FIXED_IMPACTED_CLIENT_COLUMNS);

const TEXT_COLUMN_KEYS = new Set([
    "account",
    "name",
    "advisor_name",
    "advisor_id",
    "perfil",
]);

const MONETARY_COLUMN_KEYS = new Set([
    "volumeOportunidade",
    "totalCarteira",
    "totalPrefixado",
    "volume_oportunidade",
]);

const isPercentColumn = (key: string): boolean =>
    /percent|percentual|pct|ratio/i.test(key);

const isMonetaryColumn = (key: string): boolean =>
    MONETARY_COLUMN_KEYS.has(key) ||
    /volume|amount|valor|total|net|liquido|bruto|carteira|oportunidade/i.test(key);

/** Formata célula da pré-visualização / tabelas de clientes impactados. */
export const formatImpactedClientCell = (columnKey: string, value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (TEXT_COLUMN_KEYS.has(columnKey)) return String(value);

    const num = coerceToFiniteNumber(value);
    if (num === null) {
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
    }

    if (isPercentColumn(columnKey)) return formatPercentPtBR(num);
    if (isMonetaryColumn(columnKey)) return formatCurrencyBRL(num);
    return formatNumberPtBR(num);
};

/** Colunas fixas primeiro, depois métricas explicativas (ordem estável). */
export const orderImpactedClientColumns = (row: Record<string, unknown>): string[] => {
    const keys = Object.keys(row);
    const fixed = FIXED_IMPACTED_CLIENT_COLUMNS.filter((k) => keys.includes(k));
    const extras = keys.filter((k) => !FIXED_SET.has(k) && k !== "advisor_id");
    return [...fixed, ...extras.sort()];
};

/** União de colunas de todas as linhas (fixas primeiro, depois extras). */
export const orderImpactedClientColumnsFromRows = (
    rows: Record<string, unknown>[]
): string[] => {
    if (!rows.length) return [];
    const extraSet = new Set<string>();
    for (const row of rows) {
        for (const key of Object.keys(row)) {
            if (!FIXED_SET.has(key) && key !== "advisor_id") {
                extraSet.add(key);
            }
        }
    }
    const fixed = FIXED_IMPACTED_CLIENT_COLUMNS.filter((k) =>
        rows.some((row) => k in row && row[k] !== undefined)
    );
    return [...fixed, ...Array.from(extraSet).sort()];
};

const _humanizeColumnKey = (key: string): string =>
    key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

export const impactedClientColumnLabel = (key: string): string => {
    const labels: Record<string, string> = {
        account: "Conta",
        name: "Cliente",
        advisor_name: "Assessor",
        volumeOportunidade: "Volume oportunidade",
        advisor_id: "ID assessor",
        totalCarteira: "Total carteira",
        perfil: "Perfil",
        totalPrefixado: "Total prefixado",
        totalPosfixado: "Total pós-fixado",
        totalRendaVariavel: "Total renda variável",
        totalFII: "Total FII",
        totalProduto: "Total produto",
        percentualPrefixado: "% prefixado",
        percentualPosfixado: "% pós-fixado",
        percentualRendaVariavel: "% renda variável",
        percentualFII: "% FII",
        percentualProduto: "% produto",
        percentualAlocado: "% alocado",
        desvio: "Desvio",
        maxDesvio: "Desvio máx.",
    };
    return labels[key] ?? _humanizeColumnKey(key);
};
